import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { resetStore } from '../actions/storeReset';

/**
 * A reference to one optimization snapshot for the compare view: a job plus the
 * refinement version within it (`versionNumber` 0 = the original result). Lets
 * the comparison grid mix whole jobs and specific refinement versions.
 */
export interface ComparisonVersionRef {
  jobId: number;
  /**
   * Refinement version to show. `null` means "the job's active version" —
   * produced by a plain job ID in the URL (`/compare/12`) and resolved to the
   * actual number by the comparison column. Explicit numbers (incl. 0 = original)
   * are used as-is.
   */
  versionNumber: number | null;
}

/**
 * State for Territory Management UI
 * Manages UI state for bulk upload, optimization, and other territory management features
 */
export interface TerritoryManagementState {
  /** Whether the bulk upload panel is visible */
  showBulkUpload: boolean;
  /** Files selected for bulk upload (stored for UI display, not actual upload) */
  selectedFiles: string[];
  /** Whether the optimization panel is visible */
  isOptimizing: boolean;
  /** Whether the optimization landing page is visible (vs. the wizard) */
  showOptimizeLanding: boolean;
  /** Whether the AI optimization chat is visible */
  showOptimizeChat: boolean;
  /** Whether the optimization results page is visible */
  showOptimizeResults: boolean;
  /** The job ID for the results page (NID) */
  optimizationResultsJobId: number | null;
  /** Whether the side-by-side comparison view is visible */
  showOptimizeComparison: boolean;
  /** Version refs ({jobId, versionNumber}) for side-by-side comparison (up to 3) */
  comparisonVersionRefs: ComparisonVersionRef[];
  /** Thread ID that launched the comparison view, used to navigate back on close */
  comparisonSourceThreadId: number | null;
  /** Job ID that launched the comparison view — navigate back to its results on close */
  comparisonSourceJobId: number | null;
}

const initialState: TerritoryManagementState = {
  showBulkUpload: false,
  selectedFiles: [],
  isOptimizing: false,
  showOptimizeLanding: false,
  showOptimizeChat: false,
  showOptimizeResults: false,
  optimizationResultsJobId: null,
  showOptimizeComparison: false,
  comparisonVersionRefs: [],
  comparisonSourceThreadId: null,
  comparisonSourceJobId: null,
};

export const territoryManagementSlice = createSlice({
  name: 'territoryManagement',
  initialState,
  reducers: {
    /**
     * Set whether the bulk upload panel is visible
     */
    setShowBulkUpload: (state, action: PayloadAction<boolean>) => {
      state.showBulkUpload = action.payload;
    },
    /**
     * Toggle the bulk upload panel visibility
     */
    toggleBulkUpload: (state) => {
      state.showBulkUpload = !state.showBulkUpload;
    },
    /**
     * Set selected file names for display purposes
     */
    setSelectedFiles: (state, action: PayloadAction<string[]>) => {
      state.selectedFiles = action.payload;
    },
    /**
     * Clear selected files
     */
    clearSelectedFiles: (state) => {
      state.selectedFiles = [];
    },
    /**
     * Reset bulk upload state (close panel and clear files)
     */
    resetBulkUpload: (state) => {
      state.showBulkUpload = false;
      state.selectedFiles = [];
    },
    /**
     * Set whether the optimization panel is visible.
     * Automatically resets showOptimizeLanding when closing.
     */
    setIsOptimizing: (state, action: PayloadAction<boolean>) => {
      state.isOptimizing = action.payload;
      if (!action.payload) {
        state.showOptimizeLanding = false;
        state.showOptimizeChat = false;
        state.showOptimizeResults = false;
        state.optimizationResultsJobId = null;
        state.showOptimizeComparison = false;
        state.comparisonVersionRefs = [];
        state.comparisonSourceThreadId = null;
        state.comparisonSourceJobId = null;
      }
    },
    /**
     * Set whether the optimization landing page is visible (vs. the wizard)
     */
    setShowOptimizeLanding: (state, action: PayloadAction<boolean>) => {
      state.showOptimizeLanding = action.payload;
    },
    /**
     * Set whether the AI optimization chat is visible
     */
    setShowOptimizeChat: (state, action: PayloadAction<boolean>) => {
      state.showOptimizeChat = action.payload;
    },
    setShowOptimizeResults: (state, action: PayloadAction<boolean>) => {
      state.showOptimizeResults = action.payload;
    },
    setOptimizationResultsJobId: (state, action: PayloadAction<number | null>) => {
      state.optimizationResultsJobId = action.payload;
    },
    setShowOptimizeComparison: (state, action: PayloadAction<boolean>) => {
      state.showOptimizeComparison = action.payload;
    },
    setComparisonVersionRefs: (
      state,
      action: PayloadAction<ComparisonVersionRef[]>
    ) => {
      state.comparisonVersionRefs = action.payload;
    },
    setComparisonSourceThreadId: (state, action: PayloadAction<number | null>) => {
      state.comparisonSourceThreadId = action.payload;
    },
    setComparisonSourceJobId: (state, action: PayloadAction<number | null>) => {
      state.comparisonSourceJobId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle global store reset for user impersonation
    builder.addCase(resetStore, () => initialState);
  },
});

// Export actions
export const {
  setShowBulkUpload,
  toggleBulkUpload,
  setSelectedFiles,
  clearSelectedFiles,
  resetBulkUpload,
  setIsOptimizing,
  setShowOptimizeLanding,
  setShowOptimizeChat,
  setShowOptimizeResults,
  setOptimizationResultsJobId,
  setShowOptimizeComparison,
  setComparisonVersionRefs,
  setComparisonSourceThreadId,
  setComparisonSourceJobId,
} = territoryManagementSlice.actions;

// Selectors
export const selectShowBulkUpload = (state: RootState) =>
  state.territoryManagement.showBulkUpload;

export const selectSelectedFiles = (state: RootState) =>
  state.territoryManagement.selectedFiles;

export const selectIsOptimizing = (state: RootState) =>
  state.territoryManagement.isOptimizing;

export const selectShowOptimizeLanding = (state: RootState) =>
  state.territoryManagement.showOptimizeLanding;

export const selectShowOptimizeChat = (state: RootState) =>
  state.territoryManagement.showOptimizeChat;

export const selectShowOptimizeResults = (state: RootState) =>
  state.territoryManagement.showOptimizeResults;

export const selectOptimizationResultsJobId = (state: RootState) =>
  state.territoryManagement.optimizationResultsJobId;

export const selectShowOptimizeComparison = (state: RootState) =>
  state.territoryManagement.showOptimizeComparison;

export const selectComparisonVersionRefs = (state: RootState) =>
  state.territoryManagement.comparisonVersionRefs;

export const selectComparisonSourceThreadId = (state: RootState) =>
  state.territoryManagement.comparisonSourceThreadId;

export const selectComparisonSourceJobId = (state: RootState) =>
  state.territoryManagement.comparisonSourceJobId;

// Export reducer
export default territoryManagementSlice.reducer;
